import { gql, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

const GET_SUBSCRIPTION_STATUS = gql`
  query GetSubscriptionStatus {
    mySubscriptions {
      id
      status
      expires_at
      starts_at
      package {
        id
        name
        duration_days
      }
    }
  }
`;

const SubscriptionStatusWidget = () => {
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000 // Refresh every minute
  });

  if (loading) return null;

  const activeSubscription = data?.mySubscriptions?.find(sub => sub.status === 'active');
  const pendingSubscriptions = data?.mySubscriptions?.filter(sub => sub.status === 'pending') || [];
  
  if (!activeSubscription) {
    // No active subscription - show purchase prompt
    return (
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-xl shadow-lg border border-red-400">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="material-icons-round text-xl">warning</span>
            </div>
            <div>
              <h3 className="font-semibold text-sm">No Active Subscription</h3>
              <p className="text-xs text-red-100 mt-0.5">Purchase a plan to access all features</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard', { state: { activeSection: 'order-management' } })}
            className="bg-white text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
          >
            Buy Plan
          </button>
        </div>
      </div>
    );
  }

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    let expiry;

    if (typeof expiresAt === 'string' && !isNaN(expiresAt)) {
      expiry = new Date(parseInt(expiresAt));
    } else if (typeof expiresAt === 'number') {
      expiry = new Date(expiresAt);
    } else {
      expiry = new Date(expiresAt);
    }

    if (isNaN(expiry.getTime())) return 0;

    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining(activeSubscription.expires_at);
  
  // Determine warning level
  let bgColor, textColor, iconColor, icon, message, showRenewButton;
  
  if (daysRemaining <= 0) {
    // Expired but might be in grace period
    bgColor = 'from-red-500 to-red-600';
    textColor = 'text-white';
    iconColor = 'bg-white/20';
    icon = 'error';
    message = 'Subscription Expired';
    showRenewButton = true;
  } else if (daysRemaining <= 1) {
    // Critical - expires today or tomorrow
    bgColor = 'from-red-500 to-red-600';
    textColor = 'text-white';
    iconColor = 'bg-white/20';
    icon = 'warning';
    message = daysRemaining === 0 ? 'Expires Today!' : 'Expires Tomorrow!';
    showRenewButton = true;
  } else if (daysRemaining <= 3) {
    // Urgent - 2-3 days
    bgColor = 'from-orange-500 to-orange-600';
    textColor = 'text-white';
    iconColor = 'bg-white/20';
    icon = 'schedule';
    message = `${daysRemaining} Days Remaining`;
    showRenewButton = true;
  } else if (daysRemaining <= 7) {
    // Warning - 4-7 days
    bgColor = 'from-yellow-500 to-yellow-600';
    textColor = 'text-white';
    iconColor = 'bg-white/20';
    icon = 'timer';
    message = `${daysRemaining} Days Remaining`;
    showRenewButton = true;
  } else {
    // Normal - more than 7 days
    bgColor = 'from-green-500 to-green-600';
    textColor = 'text-white';
    iconColor = 'bg-white/20';
    icon = 'check_circle';
    message = `${daysRemaining} Days Remaining`;
    showRenewButton = false;
  }

  return (
    <>
      {/* Active Subscription Widget */}
      <div className={`bg-gradient-to-r ${bgColor} ${textColor} p-4 rounded-xl shadow-lg border border-white/20`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className={`w-10 h-10 ${iconColor} rounded-lg flex items-center justify-center`}>
              <span className="material-icons-round text-xl">{icon}</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">{activeSubscription.package.name}</h3>
              <p className="text-xs opacity-90 mt-0.5">{message}</p>
            </div>
          </div>
          {showRenewButton && (
            <button
              onClick={() => navigate('/dashboard', { state: { activeSection: 'order-management' } })}
              className="bg-white/90 hover:bg-white text-gray-900 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1"
            >
              <span className="material-icons-round text-sm">autorenew</span>
              Renew
            </button>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="mt-3">
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white/80 rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.max(0, Math.min(100, (daysRemaining / activeSubscription.package.duration_days) * 100))}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Pending Subscriptions Info */}
      {pendingSubscriptions.length > 0 && (
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-3 rounded-xl shadow-lg border border-white/20 mt-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="material-icons-round text-sm">schedule</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-xs">
                {pendingSubscriptions.length} Upcoming Subscription{pendingSubscriptions.length > 1 ? 's' : ''}
              </h3>
              <p className="text-[10px] opacity-90 mt-0.5">
                Will activate when current plan expires
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard', { state: { activeSection: 'order-management' } })}
              className="bg-white/90 hover:bg-white text-purple-900 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all whitespace-nowrap"
            >
              View
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionStatusWidget;
